import ROLES from 'core/Authorization/roles';
import GENERAL from 'utils/constants/general';

const { DIVISION_ID } = GENERAL;

export const INVENTORY_TRANSFER_TRANSACTION_TYPE = 26;
export const INVENTORY_NEW_ENTRY_TRANSACTION_TYPE = 25;
export const INVENTORY_ADJUSTMENT_TRANSACTION_TYPE = 28;
export const RELATIVE_INVENTORY_ADJUSTMENT_METHOD = 6;

export const TRANSFER_SIGNATURE_ACTIONS = Object.freeze({
	RELEASE: 'released',
	AGREE: 'agreed',
	BOTH: 'both',
});

const hasValue = value => value !== null && value !== undefined;

const getTransactionField = (transaction, ...keys) => {
	for (const key of keys) {
		if (hasValue(transaction?.[key])) return transaction[key];
	}

	return undefined;
};

const hasRole = (user, roleId) => Boolean(user?.roles && user.roles[roleId]);

const hasSignatureFlag = (transaction, flag) =>
	Boolean(
		Array.isArray(transaction?.signed) &&
			transaction.signed.some(signature => signature?.[flag] === true),
	);

const getSignatureAction = ({
	canRelease,
	canAgree,
	useDualAction = false,
}) => {
	if (useDualAction && (canRelease || canAgree)) {
		return {
			signatureAction: TRANSFER_SIGNATURE_ACTIONS.BOTH,
			signatureLabel: 'Enviar',
		};
	}

	if (canRelease) {
		return {
			signatureAction: TRANSFER_SIGNATURE_ACTIONS.RELEASE,
			signatureLabel: 'Enviar',
		};
	}

	if (canAgree) {
		return {
			signatureAction: TRANSFER_SIGNATURE_ACTIONS.AGREE,
			signatureLabel: 'Recibir',
		};
	}

	return {
		signatureAction: undefined,
		signatureLabel: undefined,
	};
};

/**
 * Resolves UI capabilities for inventory transactions.
 *
 * Every inventory income/transfer is capability-managed. The API decides
 * whether a historical record requires legacy-scope remediation; the frontend
 * must never fall back to global-role authorization based on `draft` shape.
 */
export const getTransactionFlowActions = (
	transaction = {},
	{ user = {}, entity = {} } = {},
) => {
	const transactionTypeId = Number(
		getTransactionField(
			transaction,
			'typeTransaction',
			'transactionTypeId',
			'transaction_type_id',
		),
	);
	const createdBy = getTransactionField(transaction, 'createdBy', 'created_by');
	const draft = getTransactionField(transaction, 'draft');
	const inventoryMethodId = Number(
		getTransactionField(
			transaction,
			'warehouseInvIncomeMethodId',
			'warehouse_inv_income_method_id',
		),
	);
	const orderTargetId = Number(
		getTransactionField(
			transaction,
			'orderTargetId',
			'order_target_id',
		),
	);
	const isNewInventory =
		transactionTypeId === INVENTORY_NEW_ENTRY_TRANSACTION_TYPE;
	const isTransfer = transactionTypeId === INVENTORY_TRANSFER_TRANSACTION_TYPE;
	const isRelativeAdjustment = Boolean(
		transactionTypeId === INVENTORY_ADJUSTMENT_TRANSACTION_TYPE &&
			inventoryMethodId === RELATIVE_INVENTORY_ADJUSTMENT_METHOD &&
			Number.isInteger(orderTargetId) &&
			orderTargetId > 0 &&
			typeof draft === 'boolean',
	);
	const usesDraftFlow = isNewInventory || isTransfer || isRelativeAdjustment;
	const isCompleted = Boolean(
		getTransactionField(transaction, 'wtdIsClosed', 'completed'),
	);
	const isCreator = hasValue(user.id) && createdBy === user.id;
	// The API remains the authorization boundary; this only controls the UI affordance.
	const canDeleteDocument = !isCompleted && isCreator;
	const hasL1 = hasRole(user, ROLES.INVENTORY.TRANSACTION.L1);
	const hasL2 = hasRole(user, ROLES.INVENTORY.TRANSACTION.L2);
	const hasExpertL1 = hasRole(user, ROLES.EXPERT.L1_EXPERT);
	const originOwnerId = getTransactionField(
		transaction,
		'owhOwnerId',
		'owh_owner_id',
	);
	const destinationOwnerId = getTransactionField(
		transaction,
		'dwhOwnerId',
		'dwh_owner_id',
	);
	const originOwnerDivisionId = Number(
		getTransactionField(
			transaction,
			'owhOwnerDivisionId',
			'owh_owner_division_id',
		),
	);
	const destinationOwnerDivisionId = Number(
		getTransactionField(
			transaction,
			'dwhOwnerDivisionId',
			'dwh_owner_division_id',
		),
	);
	const matchesOriginEntity =
		hasValue(entity.id) && originOwnerId === entity.id;
	const matchesDestinationEntity =
		hasValue(entity.id) && destinationOwnerId === entity.id;
	const isOriginEntityOwner =
		originOwnerDivisionId === DIVISION_ID.ENTITY && matchesOriginEntity;
	const isDestinationEntityOwner =
		destinationOwnerDivisionId === DIVISION_ID.ENTITY &&
		matchesDestinationEntity;
	const isOriginExpertOwner =
		originOwnerDivisionId === DIVISION_ID.EXP &&
		hasValue(user.id) &&
		originOwnerId === user.id;
	const isDestinationExpertOwner =
		destinationOwnerDivisionId === DIVISION_ID.EXP &&
		hasValue(user.id) &&
		destinationOwnerId === user.id;
	const isOriginOwner = usesDraftFlow
		? isOriginEntityOwner || isOriginExpertOwner
		: matchesOriginEntity;
	const isDestinationOwner = usesDraftFlow
		? isDestinationEntityOwner || isDestinationExpertOwner
		: matchesDestinationEntity;
	const canAuthorizeOrigin =
		(isOriginEntityOwner && hasL2) || (isOriginExpertOwner && hasExpertL1);
	const canAuthorizeDestination =
		(isDestinationEntityOwner && hasL2) ||
		(isDestinationExpertOwner && hasExpertL1);
	const hasReleased = hasSignatureFlag(transaction, 'released');
	const hasAgreed = hasSignatureFlag(transaction, 'agreed');

	let canEditDraft;
	let canFinalizeDraft;
	let canRelease = false;
	let canAgree = false;

	if (usesDraftFlow) {
		canEditDraft = !isCompleted && draft === true && isCreator && hasL1;
		canFinalizeDraft = canEditDraft;
		canRelease =
			!isCompleted && draft === false && canAuthorizeOrigin && !hasReleased;
		canAgree =
			!isCompleted &&
			draft === false &&
			canAuthorizeDestination &&
			(!isDestinationExpertOwner || hasReleased) &&
			!hasAgreed;
	} else {
		// Compatibility adapter for historical records and other transaction
		// types. New transfer authorization must never depend on this branch.
		canEditDraft = !isCompleted && isCreator && (!isTransfer || !hasReleased);
		canFinalizeDraft = canEditDraft;
		canRelease =
			isTransfer &&
			!isCompleted &&
			!canEditDraft &&
			hasL2 &&
			matchesOriginEntity &&
			!hasReleased;
		canAgree =
			isTransfer &&
			!isCompleted &&
			hasL2 &&
			matchesDestinationEntity &&
			hasReleased &&
			!hasAgreed;
	}

	const signature = getSignatureAction({
		canRelease,
		canAgree,
		useDualAction:
			usesDraftFlow &&
			canAuthorizeOrigin &&
			canAuthorizeDestination &&
			(!isDestinationExpertOwner || hasReleased),
	});

	return {
		isNewInventory,
		isTransfer,
		isRelativeAdjustment,
		usesDraftFlow,
		isDraft: usesDraftFlow ? draft : canEditDraft,
		isCompleted,
		isCreator,
		canDeleteDocument,
		hasL1,
		hasL2,
		hasExpertL1,
		isOriginOwner,
		isDestinationOwner,
		isOriginEntityOwner,
		isDestinationEntityOwner,
		isOriginExpertOwner,
		isDestinationExpertOwner,
		hasReleased,
		hasAgreed,
		canEditDraft,
		canFinalizeDraft,
		canRelease,
		canAgree,
		canSign: canRelease || canAgree,
		...signature,
	};
};

export const shouldKeepTransferViewerOpenAfterFinalize = flowActions =>
	Boolean(flowActions?.canSign);

/**
 * Capability-managed inventory transactions send only the action authorized by
 * the API. Other legacy transaction types omit it.
 */
export const getTransactionSignatureRequestAction = flowActions =>
	flowActions?.usesDraftFlow ? flowActions.signatureAction : undefined;
